import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import RemarkPopover from '../common/RemarkPopover';
import { formatDateShortMonth, formatTimeFromISO } from '../../../utils/dateUtils';
import {
  fetchTransferLogs,
  TransferLogEntry,
} from '../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TransferLogModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number | null;
}

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const DIALOG_PAPER_SX = {
  borderRadius: '16px',
  width: 720,
  maxWidth: '95vw',
  maxHeight: '80vh',
  p: 0,
  overflow: 'hidden',
  boxShadow: '0px 12px 48px rgba(0, 0, 0, 0.14)',
} as const;

const INITIAL_VISIBLE = 5;

const HEAD_CELL_SX = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  color: '#667085',
  whiteSpace: 'nowrap' as const,
  py: 1.5,
  px: 2.5,
  borderBottom: 'none',
  background: '#F5F6F7',
};

const BODY_CELL_SX = {
  verticalAlign: 'middle' as const,
  py: 1.5,
  px: 2.5,
  fontSize: 14,
};

const ACTION_STYLES: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  assigned: { label: 'Assigned', color: '#1B5E20', bg: '#E8F5E9', icon: <ArrowForwardRoundedIcon sx={{ fontSize: 13 }} /> },
  delinked: { label: 'Delinked', color: '#BF360C', bg: '#FBE9E7', icon: <LinkOffRoundedIcon sx={{ fontSize: 13 }} /> },
};

const getAction = (a: string) =>
  ACTION_STYLES[a?.toLowerCase()] ?? { label: a || '-', color: '#616161', bg: '#F5F5F5', icon: <SwapHorizRoundedIcon sx={{ fontSize: 13 }} /> };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TransferLogModal: React.FC<TransferLogModalProps> = ({ open, onClose, invoiceId }) => {
  const [logs, setLogs] = useState<TransferLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [remarkAnchor, setRemarkAnchor] = useState<HTMLElement | null>(null);
  const [remarkText, setRemarkText] = useState('');

  const handleReasonClick = useCallback((e: React.MouseEvent<HTMLElement>, reason: string) => {
    setRemarkAnchor(e.currentTarget);
    setRemarkText(reason);
  }, []);

  const loadLogs = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTransferLogs(invoiceId);
      const sorted = [...(res.transfer_logs ?? [])].sort((a, b) => {
        const da = a.created_on ? new Date(a.created_on).getTime() : 0;
        const db = b.created_on ? new Date(b.created_on).getTime() : 0;
        return db - da;
      });
      setLogs(sorted);
    } catch {
      setError('Failed to load transfer logs.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (open && invoiceId) loadLogs();
    if (!open) { setLogs([]); setError(null); setExpanded(false); }
  }, [open, invoiceId, loadLogs]);

  const hasMore = logs.length > INITIAL_VISIBLE;
  const hiddenCount = logs.length - INITIAL_VISIBLE;
  const visible = useMemo(() => (expanded ? logs : logs.slice(0, INITIAL_VISIBLE)), [expanded, logs]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
        aria-labelledby="transfer-log-title"
        data-testid="transfer-log-modal"
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFAFA', flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SwapHorizRoundedIcon sx={{ fontSize: 20, color: '#1565C0' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography id="transfer-log-title" variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>
                  Transfer Log
                </Typography>
                {!loading && logs.length > 0 && (
                  <Chip label={logs.length} size="small" sx={{ height: 20, minWidth: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#E3F2FD', color: '#1565C0' }} />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                Assignment &amp; delink history
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ width: 30, height: 30, backgroundColor: '#f0f0f0', '&:hover': { backgroundColor: '#e0e0e0' } }}>
            <CloseRoundedIcon sx={{ fontSize: 16, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <Typography variant="body2" sx={{ color: 'error.main' }}>{error}</Typography>
            </Box>
          )}

          {!loading && !error && logs.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
              <SwapHorizRoundedIcon sx={{ fontSize: 36, color: '#E0E0E0', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>No transfer history found</Typography>
            </Box>
          )}

          {!loading && !error && visible.length > 0 && (
            <TableContainer>
              <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0, '& th, & td': { borderBottom: '1px solid', borderColor: 'divider' } }}>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-root': { borderBottom: 'none', background: '#F5F6F7' } }}>
                    <TableCell sx={{ ...HEAD_CELL_SX, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>Action</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Recipient</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>MRN</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>By</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, whiteSpace: 'nowrap' }}>Date</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, borderTopRightRadius: 12, borderBottomRightRadius: 12 }}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((entry) => {
                    const cfg = getAction(entry.action);
                    return (
                      <TableRow
                        key={entry.id}
                        hover
                        sx={{
                          height: 60,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                        }}
                      >
                        <TableCell sx={BODY_CELL_SX}>
                          <Chip
                            icon={cfg.icon as React.ReactElement}
                            label={cfg.label}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              color: cfg.color,
                              bgcolor: cfg.bg,
                              border: 'none',
                              height: 22,
                              '& .MuiChip-icon': { color: cfg.color, ml: 0.5 },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            {entry.recipient_name || '-'}
                          </Box>
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          <Box component="span" sx={{ color: '#888' }}>
                            {entry.recipient_mrn || '-'}
                          </Box>
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {entry.created_by || '-'}
                        </TableCell>
                        <TableCell sx={{ ...BODY_CELL_SX, whiteSpace: 'nowrap' }}>
                          <Box>{entry.created_on ? formatDateShortMonth(entry.created_on) : '-'}</Box>
                          <Box sx={{ color: '#888', fontSize: 13, mt: 0.25 }}>
                            {formatTimeFromISO(entry.created_on)}
                          </Box>
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {entry.reason ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <InfoOutlinedIcon
                                sx={{ fontSize: 20, color: '#5A8AEA', flexShrink: 0, cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); handleReasonClick(e as unknown as React.MouseEvent<HTMLElement>, entry.reason!); }}
                              />
                              <Box
                                component="span"
                                sx={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }}
                              >
                                {entry.reason}
                              </Box>
                            </Box>
                          ) : (
                            <Box component="span" sx={{ color: 'text.disabled' }}>-</Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Expand / Collapse */}
        {!loading && hasMore && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2.5, py: 1, display: 'flex', justifyContent: 'center', flexShrink: 0, bgcolor: '#FAFAFA' }}>
            <Button
              size="small"
              onClick={() => setExpanded((p) => !p)}
              endIcon={expanded ? <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', color: '#5A8AEA', '&:hover': { bgcolor: '#E3F2FD' } }}
              data-testid="transfer-log-expand-btn"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            </Button>
          </Box>
        )}
      </Dialog>

      <RemarkPopover
        anchorEl={remarkAnchor}
        open={Boolean(remarkAnchor)}
        title="Reason"
        content={remarkText}
        onClose={() => { setRemarkAnchor(null); setRemarkText(''); }}
      />
    </>
  );
};

export default TransferLogModal;
